import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ChevronRight } from "lucide-react";
import { SectionTitle, GlassCard } from "../components/common/ui";
import ProductCard from "../components/common/ProductCard";
import Breadcrumbs from "../components/seo/Breadcrumbs";
import ContentSections from "../components/public/ContentSections";
import RelatedContent from "../components/public/RelatedContent";
import api from "../lib/api";

// Public brand pages: an index of active brands and a per-brand product listing.
// These give brands real, indexable URLs (/brands, /brands/:slug) that the SEO
// engine and sitemap already understand.

export function BrandsIndex() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/brands").then(({ data }) => setBrands(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid="brands-index">
      <Breadcrumbs className="mb-6" />
      <SectionTitle eyebrow="Shop by" title="Fireworks Brands" subtitle="Browse our range by manufacturer." />
      <div className="mt-10">
        {brands.length === 0 ? (
          <GlassCard className="p-12 text-center text-muted-foreground" hover={false}>No brands available yet.</GlassCard>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="brands-grid">
            {brands.map((b) => (
              <Link key={b.brand_id || b.slug} to={`/brands/${b.slug}`} data-testid={`brand-tile-${b.slug}`}
                className="group rounded-xl border border-white/10 bg-white/5 hover:border-gold/40 hover:bg-white/10 transition-colors p-6 text-center">
                {b.logo ? <img src={b.logo} alt={b.name} className="h-16 mx-auto object-contain mb-3" /> : null}
                <p className="font-cinzel text-white group-hover:text-gold transition-colors">{b.name}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BrandPage() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [items, setItems] = useState([]);
  const [related, setRelated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false); setRelated(null);
    api.get(`/brands/${slug}`)
      .then(async ({ data }) => {
        setBrand(data);
        const { data: prods } = await api.get("/products", { params: { brand: data.name, limit: 48 } });
        setItems(prods.items || []);
        api.get("/related-content", { params: { entity_type: "brand", slug: data.slug } }).then(({ data: rc }) => setRelated(rc)).catch(() => {});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  if (notFound) return (
    <div className="mx-auto max-w-3xl px-4 py-32 text-center" data-testid="brand-not-found">
      <h1 className="font-playfair text-3xl text-white">Brand not found</h1>
      <Link to="/brands" className="mt-4 inline-block text-gold underline">Browse all brands</Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid="brand-page">
      <Breadcrumbs className="mb-6" />
      <SectionTitle eyebrow="Brand" title={`${brand.name} Fireworks`} subtitle={brand.description} />
      <div className="mt-10">
        {items.length === 0 ? (
          <GlassCard className="p-12 text-center text-muted-foreground" hover={false}>No products from this brand yet.</GlassCard>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="brand-grid">
            {items.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
      </div>
      <ContentSections content={brand.content_seo} />
      <RelatedContent data={related} />
    </div>
  );
}
